import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../api/client';

const POLL_INTERVAL_MS = 2000; // 2 seconds
const MAX_POLL_ATTEMPTS = 60; // 2 minutes max (60 * 2 seconds)

/**
 * Hook for fetching settled transactions with async processing support
 * Handles polling for status when backend is processing transactions asynchronously
 */
export function useSettledTransactions(username, shouldFetch = true) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(shouldFetch);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(null);
  
  // Use refs to track polling state and prevent duplicate polls
  const pollingRef = useRef(false);
  const pollAttemptsRef = useRef(0);
  const pollIntervalRef = useRef(null);

  /**
   * Poll for processing status
   */
  const pollStatus = useCallback(async (username) => {
    if (pollingRef.current) {
      return; // Already polling
    }

    pollingRef.current = true;
    pollAttemptsRef.current = 0;

    const poll = async () => {
      try {
        pollAttemptsRef.current += 1;

        // Check max attempts
        if (pollAttemptsRef.current > MAX_POLL_ATTEMPTS) {
          pollingRef.current = false;
          setError('Transaction processing timeout - please try again');
          setLoading(false);
          setProgress(null);
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          return;
        }

        // Fetch status
        const statusResponse = await apiClient.get('/api/v1/transactions/settled/status');

        const status = statusResponse.data;

        // Update progress UI
        setProgress(status);

        // Check status
        if (status.status === 'COMPLETED') {
          // Processing done - fetch transactions
          pollingRef.current = false;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }

          const finalResponse = await apiClient.get('/api/v1/transactions/settled', {
            params: { page: 0, size: 10000 },
          });
          const raw = finalResponse.data;
          const finalTransactions = (Array.isArray(raw?.items) ? raw.items : raw) || [];
          setTransactions(finalTransactions);
          setLoading(false);
          setProgress(null);
          return;
        }

        if (status.status === 'FAILED') {
          pollingRef.current = false;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setError(status.errorMessage || 'Transaction processing failed');
          setLoading(false);
          setProgress(null);
          return;
        }

        // Continue polling if PENDING or PROCESSING
        // The interval will call this function again
      } catch (err) {
        console.error('Error polling status:', err);
        
        // If we get a 401, stop polling - the global interceptor will handle auth
        if (err.response?.status === 401) {
          pollingRef.current = false;
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setLoading(false);
          return;
        }
        
        // Don't stop polling on other network errors, just log
        // The interval will retry
      }
    };

    // Start polling immediately, then every POLL_INTERVAL_MS
    await poll();
    pollIntervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
  }, []);

  /**
   * Fetch settled transactions
   */
  const fetchTransactions = useCallback(async (forceRefresh = false) => {
    if (!shouldFetch || !username) {
      setLoading(false);
      return;
    }

    // Reset state
    setLoading(true);
    setError(null);
    setProgress(null);
    setTransactions([]);

    // Stop any existing polling
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    pollingRef.current = false;
    pollAttemptsRef.current = 0;

    try {
      // Step 1: Request transactions (triggers async if needed)
      const response = await apiClient.get('/api/v1/transactions/settled', {
        params: { page: 0, size: 10000 },
      });
      const raw = response.data;
      const data = (Array.isArray(raw?.items) ? raw.items : raw) || [];

      // Step 2: If empty array, it means processing started - poll for status
      if (data.length === 0) {
        // Start polling
        await pollStatus(username);
        return;
      }

      // Step 3: If transactions returned, they were cached - return immediately
      setTransactions(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching settled transactions:', err);
      
      // Don't set error or clear state on 401 - let the global interceptor handle it
      // This prevents the component from trying to handle auth errors
      if (err.response?.status === 401) {
        // The global axios interceptor will handle the redirect
        setLoading(false);
        return;
      }
      
      // Don't show "No accounts found" errors as they're likely timing issues
      // The accounts might exist but not be detected yet
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch transactions';
      if (!errorMessage.toLowerCase().includes('no accounts found')) {
        setError(errorMessage);
      }
      setLoading(false);
      setProgress(null);
    }
  }, [username, shouldFetch, pollStatus]);

  // Stop polling if shouldFetch becomes false
  useEffect(() => {
    if (!shouldFetch) {
      // Stop any ongoing polling
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      pollingRef.current = false;
      setLoading(false);
    }
  }, [shouldFetch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      pollingRef.current = false;
    };
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  // Refetch function that bypasses cache
  const refetch = useCallback(() => {
    return fetchTransactions(true);
  }, [fetchTransactions]);

  return { 
    transactions, 
    loading, 
    error, 
    progress, 
    refetch 
  };
}

